import { useEditProtection } from '../../shared/components/EditProtection';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import ProfileInviteButton from './ProfileInviteButton';
import RateHistoryPanel from './RateHistoryPanel';
import type { CloverEmployee } from '../salary/types';
import '../salary/SalaryModule.css';
import './Profile.css';
import { useAuth } from '../../contexts/AuthContext';

interface ProfileResponse {
  user_id: number | null;
  username: string;
  name: string;
  role: string;
  employee_id: number | null;
  employee_name: string | null;
  address: string | null;
  email: string | null;
  phone: string | null;
  dob: string | null;
  hire_date: string | null;
  sin_last3: string | null;
  sin_expiry_date: string | null;
  residency_status: string | null;
  has_address_proof: boolean;
  address_proof_uploaded_at: string | null;
  has_residency_proof: boolean;
  residency_proof_uploaded_at: string | null;
  has_full_sin: boolean;
  is_cash_employee: boolean;
  default_hourly_rate: number | string;
  clover_employee_id: string | null;
  is_active: boolean;
  last_working_day: string | null;
}

const RESIDENCY_OPTIONS: { value: string; label: string }[] = [
  { value: 'canadian_citizen', label: 'Canadian Citizen' },
  { value: 'permanent_resident', label: 'Permanent Resident' },
  { value: 'work_permit', label: 'Work Permit' },
  { value: 'study_permit', label: 'Study Permit' },
  { value: 'other', label: 'Other' },
];

async function api<T = any>(path: string, sessionToken: string | null, init?: RequestInit): Promise<T> {
  const res = await fetch(`/api${path}`, {
    cache: 'no-store',
    ...init,
    headers: {
      'Content-Type': 'application/json',
      'X-Session-Token': sessionToken || '',
      ...(init?.headers || {}),
    },
  });
  const text = await res.text();
  const data = text ? JSON.parse(text) : {};
  if (!res.ok) throw new Error(data.error || `${res.status} ${path}`);
  return data;
}

function fmtDateTime(iso: string | null): string {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

const ProfileEditor: React.FC = () => {
  const { sessionToken, user } = useAuth();
  const [params] = useSearchParams();
  const targetUser = params.get('userId');
  const targetEmployee = params.get('employeeId');
  const targetQuery = targetEmployee !== null ? `employeeId=${encodeURIComponent(targetEmployee)}`
    : targetUser !== null ? `userId=${encodeURIComponent(targetUser)}` : '';
  const path = useCallback((suffix = '') => `/profile${suffix}${targetQuery ? `?${targetQuery}` : ''}`, [targetQuery]);
  const manage = user?.role === 'Owner' || user?.role === 'Admin';
  const [username, setUsername] = useState('');
  const [isCash, setIsCash] = useState(false);
  const [hourlyRate, setHourlyRate] = useState('');
  const [cloverId, setCloverId] = useState('');
  const [sinLast3, setSinLast3] = useState('');
  const [cloverEmployees, setCloverEmployees] = useState<CloverEmployee[]>([]);
  const [cloverErr, setCloverErr] = useState('');
  const [statusDate, setStatusDate] = useState('');
  const [statusBusy, setStatusBusy] = useState(false);
  const [profile, setProfile] = useState<ProfileResponse | null>(null);
  const [loadErr, setLoadErr] = useState('');
  const [saveErr, setSaveErr] = useState('');
  const [saveMsg, setSaveMsg] = useState('');
  const [saving, setSaving] = useState(false);

  // editable fields
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [dob, setDob] = useState('');
  const [hireDate, setHireDate] = useState('');
  const [address, setAddress] = useState('');
  const [residencyStatus, setResidencyStatus] = useState('');
  const [sinExpiry, setSinExpiry] = useState('');

  // SIN handling
  const [revealedSin, setRevealedSin] = useState<string | null>(null);
  const [showSinEditor, setShowSinEditor] = useState(false);
  const [sinDraft, setSinDraft] = useState('');
  const [sinBusy, setSinBusy] = useState(false);

  // doc uploads
  const [uploadingKind, setUploadingKind] = useState<string | null>(null);
  const addressFileRef = useRef<HTMLInputElement>(null);
  const residencyFileRef = useRef<HTMLInputElement>(null);

  // password change
  const [pwCurrent, setPwCurrent] = useState('');
  const [pwNew, setPwNew] = useState('');
  const [pwConfirm, setPwConfirm] = useState('');
  const [pwBusy, setPwBusy] = useState(false);
  const [pwMsg, setPwMsg] = useState('');
  const [pwErr, setPwErr] = useState('');

  const [baseline, setBaseline] = useState('');
  const draftSnapshot = JSON.stringify({ name, phone, email, dob, hireDate, address, residencyStatus, sinExpiry, username, isCash, hourlyRate, cloverId, sinLast3 });
  useEditProtection(!!profile && !!baseline && (baseline !== draftSnapshot || !!sinDraft || !!pwNew));
  useEffect(() => { if (profile && !baseline) setBaseline(draftSnapshot); }, [profile, baseline, draftSnapshot]);
  const reload = useCallback(async () => {
    try {
      const data = await api<{ profile: ProfileResponse }>(path(), sessionToken);
      setLoadErr('');
      setProfile(data.profile);
      setUsername(data.profile.username || '');
      setIsCash(data.profile.is_cash_employee || false);
      setHourlyRate(String(data.profile.default_hourly_rate ?? '17.60'));
      setCloverId(data.profile.clover_employee_id || '');
      setSinLast3(data.profile.sin_last3 || '');
      setName(data.profile.employee_name || data.profile.name || '');
      setPhone(data.profile.phone || '');
      setEmail(data.profile.email || '');
      setDob(data.profile.dob ? data.profile.dob.slice(0, 10) : '');
      setHireDate(data.profile.hire_date ? data.profile.hire_date.slice(0, 10) : '');
      setAddress(data.profile.address || '');
      setResidencyStatus(data.profile.residency_status || '');
      setSinExpiry(data.profile.sin_expiry_date ? data.profile.sin_expiry_date.slice(0, 10) : '');
      setRevealedSin(null);
    } catch (e: any) {
      setLoadErr(e.message);
    }
  }, [sessionToken, path]);

  useEffect(() => {
    setProfile(null);
    setLoadErr('');
    setSaveErr('');
    setSaveMsg('');
    setRevealedSin(null);
    setSinDraft('');
    setShowSinEditor(false);
    reload();
  }, [reload]);

  useEffect(() => {
    if (!manage) return;
    let cancelled = false;
    api<{ employees: CloverEmployee[] }>('/clover/employees', sessionToken)
      .then((data) => { if (!cancelled) setCloverEmployees(data.employees || []); })
      .catch((e: Error) => { if (!cancelled) setCloverErr(e.message); });
    return () => { cancelled = true; };
  }, [manage, sessionToken]);

  // Required-field validation. SIN expiry is only required for temporary status;
  // address & residency proofs are uploaded separately so we check
  // profile.has_address_proof / has_residency_proof on the loaded profile.
  // SIN counts as "filled" if it's already encrypted (has_full_sin) — we don't
  // require re-entering it on every save.
  const validate = (section: string): string | null => {
    if (section === 'account') return username && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(username) ? 'Login email must be valid' : null;
    if (section === 'address' || section === 'documents') return null;
    if (section !== 'employment') {
      if (!name.trim()) return 'Name is required';
      if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return 'Email must be a valid address';
    }
    if (manage && (section === 'employment' || section === 'all') && (!Number.isFinite(Number(hourlyRate)) || Number(hourlyRate) <= 0)) return 'Hourly rate must be positive';
    return null;
  };

  const save = async (section = 'all') => {
    try {
      setSaving(true);
      setSaveErr('');
      setSaveMsg('');
      const err = validate(section);
      if (err) { setSaveErr(err); setSaving(false); return; }
      const body: any = {
        name: name.trim(),
        phone: phone.trim(),
        email: email.trim(),
        dob,
        hire_date: hireDate,
        address: address.trim(),
        residency_status: residencyStatus,
        sin_expiry_date: sinExpiry || null,
      };
      if (manage) {
        if (profile?.user_id && username !== profile.username) body.username = username;
        body.is_cash_employee = isCash;
        body.clover_employee_id = cloverId || null;
        if (Number(hourlyRate) !== Number(profile?.default_hourly_rate)) body.default_hourly_rate = Number(hourlyRate);
        if (!profile?.has_full_sin) body.sin_last3 = sinLast3 || null;
      }
      const sections: Record<string, string[]> = {
        personal: ['name', 'phone', 'email', 'dob', 'hire_date'],
        address: ['address'], documents: ['residency_status', 'sin_expiry_date'],
        employment: ['is_cash_employee', 'clover_employee_id', 'default_hourly_rate', 'sin_last3'],
        account: ['username'],
      };
      const payload = section === 'all' ? body : Object.fromEntries(Object.entries(body).filter(([key]) => sections[section]?.includes(key)));
      await api(path(), sessionToken, { method: 'PUT', body: JSON.stringify(payload) });
      // Update only saved fields; unrelated unsaved sections stay intact.
      const fields: Record<string, string> = { hire_date: 'hireDate', residency_status: 'residencyStatus', sin_expiry_date: 'sinExpiry', is_cash_employee: 'isCash', clover_employee_id: 'cloverId', default_hourly_rate: 'hourlyRate', sin_last3: 'sinLast3' };
      setBaseline(previous => { const next = JSON.parse(previous || '{}'); const current = JSON.parse(draftSnapshot); Object.keys(payload).forEach(key => { next[fields[key] || key] = current[fields[key] || key]; }); return JSON.stringify(next); });
      setSaveMsg('Saved.');
      const data = await api<{ profile: ProfileResponse }>(path(), sessionToken);
      setProfile(data.profile);
    } catch (e: any) {
      setSaveErr(e.message);
    } finally {
      setSaving(false);
    }
  };

  const refreshDocuments = async () => {
    const data = await api<{ profile: ProfileResponse }>(path(), sessionToken);
    setProfile(data.profile);
    setRevealedSin(null);
    setSinLast3(data.profile.sin_last3 || '');
    setBaseline(previous => previous ? JSON.stringify({ ...JSON.parse(previous), sinLast3: data.profile.sin_last3 || '' }) : previous);
  };

  const revealSin = async () => {
    try {
      setSinBusy(true);
      const data = await api<{ sin: string | null }>(path('/sin'), sessionToken);
      setRevealedSin(data.sin || '(not set)');
    } catch (e: any) {
      setSaveErr(e.message);
    } finally {
      setSinBusy(false);
    }
  };

  const submitNewSin = async () => {
    try {
      setSinBusy(true);
      setSaveErr('');
      const clean = sinDraft.replace(/\D/g, '');
      if (clean.length !== 9) {
        setSaveErr('SIN must be 9 digits');
        return;
      }
      await api(path(), sessionToken, { method: 'PUT', body: JSON.stringify({ sin: clean }) });
      setSinDraft('');
      setShowSinEditor(false);
      setRevealedSin(null);
      setSaveMsg('SIN updated.');
      await refreshDocuments();
    } catch (e: any) {
      setSaveErr(e.message);
    } finally {
      setSinBusy(false);
    }
  };

  const uploadDoc = async (kind: 'address_proof' | 'residency_proof', file: File) => {
    try {
      setUploadingKind(kind);
      setSaveErr('');
      if (file.size > 10 * 1024 * 1024) {
        setSaveErr('File too large (max 10MB)');
        return;
      }
      const allowed = ['application/pdf', 'image/jpeg', 'image/png'];
      if (!allowed.includes(file.type)) {
        setSaveErr('File must be PDF, JPEG, or PNG');
        return;
      }
      const buf = await file.arrayBuffer();
      // Convert to base64 in chunks to avoid stack overflow
      const bytes = new Uint8Array(buf);
      let bin = '';
      const chunk = 0x8000;
      for (let i = 0; i < bytes.length; i += chunk) {
        bin += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + chunk)));
      }
      const base64 = btoa(bin);
      await api(path('/upload-doc'), sessionToken, {
        method: 'POST',
        body: JSON.stringify({ kind, content_type: file.type, file_base64: base64 }),
      });
      setSaveMsg(`${kind === 'address_proof' ? 'Address proof' : 'Residency proof'} uploaded.`);
      await refreshDocuments();
    } catch (e: any) {
      setSaveErr(e.message);
    } finally {
      setUploadingKind(null);
    }
  };

  const docUrl = (kind: 'address_proof' | 'residency_proof') =>
    `/api${path(`/doc/${kind}`)}${targetQuery ? '&' : '?'}token=${encodeURIComponent(sessionToken || '')}`;

  const changePassword = async () => {
    try {
      setPwBusy(true);
      setPwErr('');
      setPwMsg('');
      if (pwNew.length < 12) { setPwErr('Password must be at least 12 characters'); return; }
      if (!/[a-z]/.test(pwNew) || !/[A-Z]/.test(pwNew) || !/[0-9]/.test(pwNew)) {
        setPwErr('Password must include lowercase, uppercase, and a number');
        return;
      }
      if (pwNew !== pwConfirm) { setPwErr('New password and confirmation do not match'); return; }
      await api('/change-password', sessionToken, {
        method: 'POST',
        body: JSON.stringify({ currentPassword: pwCurrent, newPassword: pwNew }),
      });
      setPwMsg('Password changed.');
      setPwCurrent(''); setPwNew(''); setPwConfirm('');
    } catch (e: any) {
      setPwErr(e.message);
    } finally {
      setPwBusy(false);
    }
  };

  const initializeDetails = async () => {
    try {
      setSaving(true);
      setSaveErr('');
      await api(path(), sessionToken, { method: 'POST', body: JSON.stringify({}) });
      await reload();
    } catch (e: any) { setSaveErr(e.message); }
    finally { setSaving(false); }
  };

  const refreshRate = async () => {
    try {
      const data = await api<{ profile: ProfileResponse }>(path(), sessionToken);
      setHourlyRate(String(data.profile.default_hourly_rate));
      setProfile((previous) => previous ? { ...previous, default_hourly_rate: data.profile.default_hourly_rate } : previous);
    } catch (e: any) { setSaveErr(e.message); }
  };

  const changeStatus = async () => {
    if (!profile) return;
    try {
      setStatusBusy(true);
      setSaveErr('');
      if (profile.is_active && !statusDate) throw new Error('Last working day is required');
      if (profile.user_id) {
        await api(`/users/${profile.user_id}/deactivate${profile.is_active ? '' : '?restore=true'}`, sessionToken, {
          method: 'DELETE', body: JSON.stringify({ last_working_day: statusDate }),
        });
      } else {
        await api(path(), sessionToken, { method: 'PUT', body: JSON.stringify({ is_active: !profile.is_active, last_working_day: statusDate || null }) });
      }
      await reload();
      setSaveMsg('Employment status updated.');
    } catch (e: any) { setSaveErr(e.message); }
    finally { setStatusBusy(false); }
  };

  if (loadErr) return <div className="error-banner">{loadErr}</div>;
  if (!profile) return <p>Loading…</p>;

  const noEmployeeRow = profile.employee_id === null;

  return (
    <div className="profile-page" style={{ maxWidth: 760 }}>
      {manage && <Link to="/users">← User profiles</Link>}
      <h2>{targetQuery ? `User Profile: ${profile.employee_name || profile.name}` : 'My Profile'}</h2>

      {manage && profile.user_id && profile.employee_id && <ProfileInviteButton employeeId={profile.employee_id} />}

      {saveErr && <div className="error-banner" role="alert">{saveErr}</div>}
      {saveMsg && <div className="info-banner" style={{ background: '#e3f9e5', borderLeft: '4px solid #4caf50', padding: '0.5rem 1rem', marginBottom: '1rem' }}>{saveMsg}</div>}

      {noEmployeeRow && (
        <div className="info-banner" style={{ background: '#fff7e6', borderLeft: '4px solid #ff9800', padding: '0.75rem 1rem', marginBottom: '1rem' }}>
          Employment details have not been set up for this user.
          {manage ? <button className="salary-btn" onClick={initializeDetails} disabled={saving}>Set up employment details</button> : ' Contact an Owner or Admin to set them up.'}
        </div>
      )}

      {!noEmployeeRow && (
        <p style={{ color: '#666', marginTop: 0 }}>
          You can save each section now. Name is required; starred details are needed to complete your profile.
        </p>
      )}

      <section className="salary-card">
        <details><summary>Profile completion</summary><p>You can save sections now and complete missing details later.</p><ul>{[['Email', !!profile.email], ['Phone', !!profile.phone], ['Date of birth', !!profile.dob], ['Start date', !!profile.hire_date], ['Address', !!profile.address], ['SIN', profile.has_full_sin], ['Address proof', profile.has_address_proof], ['Residency proof', profile.has_residency_proof]].map(([label, done]) => <li key={String(label)}>{done ? '✓' : '○'} {label}</li>)}</ul></details>
        <nav className="cs-actions" aria-label="Profile sections"><a href="#profile-personal">Personal</a><a href="#profile-address-section">Address</a><a href="#profile-documents">Documents</a>{manage && <a href="#profile-employment">Employment &amp; pay</a>}</nav>
        <h3>Account</h3>
        <div className="form-row"><label>Login email<input type="email" value={username} onChange={(e) => setUsername(e.target.value)} disabled={!manage || !profile.user_id} /></label>{!profile.user_id && <small>No login account linked.</small>}</div>
        <div className="form-row"><label>Role</label><input value={profile.role || 'No login'} disabled /></div>
        {manage && <button className="salary-btn" disabled={saving || noEmployeeRow} onClick={() => save('account')}>Save account</button>}
      </section>
      <section className="salary-card" id="profile-personal"><h3>Personal details</h3>
        <div className="form-row">
          <label htmlFor="profile-name">Name <span style={{ color: '#d32f2f' }}>*</span></label>
          <input id="profile-name" value={name} onChange={(e) => setName(e.target.value)} disabled={noEmployeeRow} required />
        </div>
        <div className="form-row">
          <label htmlFor="profile-email">Email {!manage && <span style={{ color: '#d32f2f' }}>*</span>}</label>
          <input id="profile-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} disabled={noEmployeeRow} required />
        </div>
        <div className="form-row">
          <label htmlFor="profile-phone">Phone {!manage && <span style={{ color: '#d32f2f' }}>*</span>}</label>
          <input id="profile-phone" type="tel" autoComplete="tel" value={phone} onChange={(e) => setPhone(e.target.value)} disabled={noEmployeeRow} required />
        </div>
        <div className="form-row">
          <label htmlFor="profile-dob">Date of birth {!manage && <span style={{ color: '#d32f2f' }}>*</span>}</label>
          <input id="profile-dob" type="date" value={dob} onChange={(e) => setDob(e.target.value)} disabled={noEmployeeRow} required />
        </div>
        <div className="form-row">
          <label htmlFor="profile-hireDate">Start date {!manage && <span style={{ color: '#d32f2f' }}>*</span>}</label>
          <input id="profile-hireDate" type="date" value={hireDate} onChange={(e) => setHireDate(e.target.value)} disabled={noEmployeeRow} required />
          <small style={{ display: 'block', color: '#666', marginTop: 4 }}>
            The day you started at Chai Social.
          </small>
        </div>
        <button className="salary-btn" disabled={saving || noEmployeeRow} onClick={() => save('personal')}>Save personal details</button>
      </section>

      <section className="salary-card" id="profile-address-section">
        <h3>Address</h3>
        <div className="form-row">
          <label htmlFor="profile-address">Address {!manage && <span style={{ color: '#d32f2f' }}>*</span>}</label>
          <textarea id="profile-address"
            rows={2}
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            disabled={noEmployeeRow}
          />
        </div>
        <div className="form-row">
          <label htmlFor="profile-address-proof">Address proof (PDF / JPEG / PNG, max 10MB) {!manage && <span style={{ color: '#d32f2f' }}>*</span>}</label>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              type="file"
              accept="application/pdf,image/jpeg,image/png"
              id="profile-address-proof" ref={addressFileRef}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) uploadDoc('address_proof', f);
                if (addressFileRef.current) addressFileRef.current.value = '';
              }}
              disabled={noEmployeeRow || uploadingKind === 'address_proof'}
            />
            {uploadingKind === 'address_proof' && <small>Uploading…</small>}
            {profile.has_address_proof && (
              <a href={docUrl('address_proof')} target="_blank" rel="noreferrer">View current</a>
            )}
            {profile.has_address_proof && (
              <small style={{ color: '#666' }}>uploaded {fmtDateTime(profile.address_proof_uploaded_at)}</small>
            )}
          </div>
        </div>
        <button className="salary-btn" disabled={saving || noEmployeeRow} onClick={() => save('address')}>Save address</button>
      </section>

      <section className="salary-card" id="profile-documents">
        <h3>Documents &amp; residency</h3>

        <div className="form-row">
          <label>SIN {!manage && <span style={{ color: '#d32f2f' }}>*</span>}</label>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <code style={{ padding: '0.25rem 0.5rem', background: '#f5f5f5', borderRadius: 4 }}>
              {revealedSin
                ? revealedSin.replace(/(\d{3})(\d{3})(\d{3})/, '$1-$2-$3')
                : profile.has_full_sin
                  ? `XXX-XX-X${profile.sin_last3 || '???'}`
                  : profile.sin_last3
                    ? `XXX-XX-X${profile.sin_last3}`
                    : '(not set)'}
            </code>
            {profile.has_full_sin && !revealedSin && (
              <button className="salary-btn secondary" onClick={revealSin} disabled={sinBusy}>
                {sinBusy ? '…' : 'Show'}
              </button>
            )}
            {revealedSin && (
              <button className="salary-btn secondary" onClick={() => setRevealedSin(null)}>Hide</button>
            )}
            <button
              className="salary-btn secondary"
              onClick={() => { setShowSinEditor(!showSinEditor); setSinDraft(''); }}
              disabled={noEmployeeRow}
            >
              {showSinEditor ? 'Cancel' : (profile.has_full_sin ? 'Update' : 'Add')}
            </button>
          </div>
          {showSinEditor && (
            <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <input
                type="text"
                inputMode="numeric" autoComplete="off" aria-label="New SIN" placeholder="123456789"
                value={sinDraft}
                onChange={(e) => setSinDraft(e.target.value)}
                maxLength={11}
                style={{ maxWidth: 160 }}
              />
              <button className="salary-btn" onClick={submitNewSin} disabled={sinBusy}>
                {sinBusy ? 'Saving…' : 'Save SIN'}
              </button>
              <small style={{ color: '#666' }}>Stored encrypted; viewable by this user, Owners and Admins.</small>
            </div>
          )}
        </div>

        <div className="form-row">
          <label htmlFor="profile-residencyStatus">Residency status {!manage && <span style={{ color: '#d32f2f' }}>*</span>}</label>
          <select id="profile-residencyStatus"
            value={residencyStatus}
            onChange={(e) => setResidencyStatus(e.target.value)}
            disabled={noEmployeeRow}
          >
            <option value="">— select —</option>
            {RESIDENCY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>

        <div className="form-row">
          <label htmlFor="profile-sinExpiry">SIN expiry date {(['work_permit', 'study_permit'].includes(residencyStatus)) && <small style={{ color: '#b54708' }}> (required for temporary status)</small>}</label>
          <input id="profile-sinExpiry"
            type="date"
            value={sinExpiry}
            onChange={(e) => setSinExpiry(e.target.value)}
            disabled={noEmployeeRow}
          />
        </div>

        <div className="form-row">
          <label>Residency proof (PDF / JPEG / PNG, max 10MB) {!manage && <span style={{ color: '#d32f2f' }}>*</span>}</label>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              type="file"
              accept="application/pdf,image/jpeg,image/png"
              ref={residencyFileRef}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) uploadDoc('residency_proof', f);
                if (residencyFileRef.current) residencyFileRef.current.value = '';
              }}
              disabled={noEmployeeRow || uploadingKind === 'residency_proof'}
            />
            {uploadingKind === 'residency_proof' && <small>Uploading…</small>}
            {profile.has_residency_proof && (
              <a href={docUrl('residency_proof')} target="_blank" rel="noreferrer">View current</a>
            )}
            {profile.has_residency_proof && (
              <small style={{ color: '#666' }}>uploaded {fmtDateTime(profile.residency_proof_uploaded_at)}</small>
            )}
          </div>
        </div>
        <button className="salary-btn" disabled={saving || noEmployeeRow} onClick={() => save('documents')}>Save residency details</button>
      </section>

      {manage && !noEmployeeRow && (
        <section className="salary-card">
          <h3 id="profile-employment">Employment &amp; pay</h3>
          <div className="form-row"><label><input type="checkbox" checked={isCash} onChange={(e) => setIsCash(e.target.checked)} /> Cash employee (no deductions, excluded from CRA reports)</label></div>
          {!profile.has_full_sin && <div className="form-row"><label>SIN last 3 digits<input value={sinLast3} maxLength={3} onChange={(e) => setSinLast3(e.target.value.replace(/\D/g, ''))} /></label></div>}
          <div className="form-row"><label>Current hourly rate ($/hr)<input type="number" min="0.01" step="0.01" value={hourlyRate} onChange={(e) => setHourlyRate(e.target.value)} /></label><small>A changed rate takes effect today. Use pay rate history for a different effective date.</small></div>
          <div className="form-row"><label>Map to Clover employee
            <select value={cloverId} onChange={(e) => setCloverId(e.target.value)} disabled={!!cloverErr}>
              <option value="">— not mapped —</option>
              {cloverId && !cloverEmployees.some((employee) => employee.id === cloverId) && <option value={cloverId}>{cloverId}</option>}
              {cloverEmployees.map((employee) => <option key={employee.id} value={employee.id}>{employee.name}{employee.nickname ? ` (${employee.nickname})` : ''}</option>)}
            </select>
          </label>{cloverErr && <small>Clover unavailable: {cloverErr}</small>}<small>Mapped employees sync hours and wage rates from Clover on unpaid periods.</small></div>
          <div className="form-row"><span>Status: {profile.is_active ? 'Active' : 'Inactive'}</span>{profile.last_working_day && <span>Last working day: {profile.last_working_day.slice(0, 10)}</span>}</div>
          {profile.user_id !== user?.id && (user?.role === 'Owner' || profile.role !== 'Owner') && <div className="form-row">
            {profile.is_active && <label>Last working day<input type="date" value={statusDate} onChange={(e) => setStatusDate(e.target.value)} /></label>}
            <button className="salary-btn secondary" onClick={changeStatus} disabled={statusBusy || baseline !== draftSnapshot}>{profile.is_active ? 'Deactivate' : 'Reactivate'}</button>
          </div>}
          <button className="salary-btn" disabled={saving} onClick={() => save('employment')}>Save employment &amp; pay</button>
        </section>
      )}

      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem' }}>
        <button className="salary-btn" onClick={() => save()} disabled={saving || noEmployeeRow}>
          {saving ? 'Saving…' : 'Save profile'}
        </button>
      </div>

      {manage && profile.employee_id && <RateHistoryPanel key={`${profile.employee_id}:${profile.default_hourly_rate}`} employeeId={profile.employee_id} onChanged={refreshRate} />}
      {profile.user_id === user?.id && <section className="salary-card">
        <h3>Change password</h3>
        {pwErr && <div className="error-banner">{pwErr}</div>}
        {pwMsg && <div className="info-banner" style={{ background: '#e3f9e5', borderLeft: '4px solid #4caf50', padding: '0.5rem 1rem', marginBottom: '0.5rem' }}>{pwMsg}</div>}
        <div className="form-row">
          <label>Current password</label>
          <input type="password" value={pwCurrent} onChange={(e) => setPwCurrent(e.target.value)} autoComplete="current-password" />
        </div>
        <div className="form-row">
          <label>New password</label>
          <input type="password" value={pwNew} onChange={(e) => setPwNew(e.target.value)} autoComplete="new-password" />
        </div>
        <div className="form-row">
          <label>Confirm new password</label>
          <input type="password" value={pwConfirm} onChange={(e) => setPwConfirm(e.target.value)} autoComplete="new-password" />
        </div>
        <button className="salary-btn" onClick={changePassword} disabled={pwBusy || !pwCurrent || !pwNew}>
          {pwBusy ? 'Changing…' : 'Change password'}
        </button>
      </section>}
    </div>
  );
};

// Remount when switching targets so drafts or late responses cannot cross profiles.
const Profile: React.FC = () => {
  const [params] = useSearchParams();
  return <ProfileEditor key={`${params.get('userId') || ''}:${params.get('employeeId') || ''}`} />;
};
export default Profile;
